/**
 * Pure, React-free helpers for the Task domain: status/priority metadata,
 * id generation, blank-draft factories, validation, and the grouping /
 * filtering / option-collecting helpers the board and list views rely on.
 */

import { v4 as uuid } from 'uuid';
import type { BadgeTone } from '../../ui';
import type {
  BoardStatus,
  Task,
  TaskDraft,
  TaskFilter,
  TaskPriority,
  TaskStatus,
  TaskType,
} from './types';

/** Status metadata in Kanban column order (drives columns + badges). */
export const TASK_STATUSES: {
  value: TaskStatus;
  label: string;
  tone: BadgeTone;
}[] = [
  { value: 'BACKLOG', label: 'Backlog', tone: 'neutral' },
  { value: 'TODO', label: 'To Do', tone: 'info' },
  { value: 'IN_PROGRESS', label: 'In Progress', tone: 'accent' },
  { value: 'BLOCKED', label: 'Blocked', tone: 'danger' },
  { value: 'DONE', label: 'Done', tone: 'success' },
];

/** The ordered list of statuses (also the Kanban column order). */
export const STATUS_ORDER: TaskStatus[] = TASK_STATUSES.map((s) => s.value);

/** The default status for a newly-created task. */
export const DEFAULT_STATUS: TaskStatus = 'BACKLOG';

/** Priority metadata (drives selects + badges); undefined priority = "none". */
export const TASK_PRIORITIES: {
  value: TaskPriority;
  label: string;
  tone: BadgeTone;
  /** Sort weight (higher = more urgent). */
  weight: number;
}[] = [
  { value: 'URGENT', label: 'Urgent', tone: 'danger', weight: 4 },
  { value: 'HIGH', label: 'High', tone: 'warning', weight: 3 },
  { value: 'MEDIUM', label: 'Medium', tone: 'info', weight: 2 },
  { value: 'LOW', label: 'Low', tone: 'neutral', weight: 1 },
];

/** Human-readable label for a status. */
export function statusLabel(status: TaskStatus): string {
  return TASK_STATUSES.find((s) => s.value === status)?.label ?? status;
}

/** Badge tone for a status. */
export function statusTone(status: TaskStatus): BadgeTone {
  return TASK_STATUSES.find((s) => s.value === status)?.tone ?? 'neutral';
}

/** Metadata for a priority (or undefined when none is set). */
export function priorityMeta(priority?: TaskPriority) {
  if (!priority) return undefined;
  return TASK_PRIORITIES.find((p) => p.value === priority);
}

/** True when a string is a recognized status. */
export function isTaskStatus(value: string): value is TaskStatus {
  return STATUS_ORDER.includes(value as TaskStatus);
}

/** The recognized Sprint Board work-item types, in a sensible display order. */
export const TASK_TYPES: TaskType[] = ['Task', 'Story', 'Bug', 'Spike'];

/** The Sprint Board lanes, in board (left-to-right) order. */
export const BOARD_STATUSES: { value: BoardStatus; label: string }[] = [
  { value: 'TODO', label: 'To Do' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'DONE', label: 'Done' },
];

/** The ordered list of board lanes (also the board column order). */
export const BOARD_STATUS_ORDER: BoardStatus[] = BOARD_STATUSES.map(
  (s) => s.value,
);

/** True when a string is a recognized work-item type. */
export function isTaskType(value: string): value is TaskType {
  return TASK_TYPES.includes(value as TaskType);
}

/** True when a string is a recognized Sprint Board lane. */
export function isBoardStatus(value: string): value is BoardStatus {
  return BOARD_STATUS_ORDER.includes(value as BoardStatus);
}

/** Human-readable label for a Sprint Board lane. */
export function boardStatusLabel(status: BoardStatus): string {
  return BOARD_STATUSES.find((s) => s.value === status)?.label ?? status;
}

/** Deterministic-ish document id for a new task. */
export function taskDocId(): string {
  return `task_${uuid()}`;
}

/** Creates an empty draft for the create form. */
export function blankTaskDraft(status: TaskStatus = DEFAULT_STATUS): TaskDraft {
  return {
    title: '',
    description: '',
    status,
    assignee: '',
    priority: undefined,
    estimateId: '',
    tags: [],
  };
}

/** Projects an existing task into an editable draft. */
export function draftFromTask(task: Task): TaskDraft {
  return {
    title: task.title,
    description: task.description ?? '',
    status: task.status,
    assignee: task.assignee ?? '',
    priority: task.priority,
    estimateId: task.estimateId ?? '',
    tags: task.tags ?? [],
    type: task.type ?? 'Task',
    sprintId: task.sprintId ?? null,
    boardStatus: task.boardStatus ?? 'TODO',
    blocked: task.blocked ?? false,
    blocker: task.blocker,
    storyPoints: task.storyPoints,
    effortDays: task.effortDays,
    confidence: task.confidence,
    goalId: task.goalId ?? '',
    workstreamId: task.workstreamId ?? '',
    serviceId: task.serviceId ?? '',
    owner: task.owner ?? '',
    context: task.context ?? '',
    parentTaskId: task.parentTaskId ?? '',
    actualEffortDays: task.actualEffortDays,
    completionNote: task.completionNote ?? '',
    adhoc: task.adhoc ?? false,
    addedToSprintAt: task.addedToSprintAt,
  };
}

/**
 * Parses a comma/whitespace separated tag string into a normalized, de-duped
 * list (trimmed, empties dropped, original order preserved).
 */
export function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins tags back into a comma-separated string for the tag input. */
export function tagsToInput(tags?: string[]): string {
  return (tags ?? []).join(', ');
}

/**
 * Validates a draft for save. Returns a list of human-readable problems; an
 * empty list means the draft is valid.
 */
export function validateTaskDraft(draft: TaskDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('Task title is required.');
  if (!isTaskStatus(draft.status)) problems.push('A valid status is required.');
  return problems;
}

/** Groups tasks by status into an ordered, column-keyed map. */
export function groupByStatus(tasks: Task[]): Record<TaskStatus, Task[]> {
  const groups = {
    BACKLOG: [],
    TODO: [],
    IN_PROGRESS: [],
    BLOCKED: [],
    DONE: [],
  } as Record<TaskStatus, Task[]>;
  for (const task of tasks) {
    (groups[task.status] ?? groups.BACKLOG).push(task);
  }
  return groups;
}

/** Sorts tasks by priority weight (desc), then most-recently-updated first. */
export function sortTasks(tasks: Task[]): Task[] {
  return tasks.slice().sort((a, b) => {
    const pa = priorityMeta(a.priority)?.weight ?? 0;
    const pb = priorityMeta(b.priority)?.weight ?? 0;
    if (pb !== pa) return pb - pa;
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
  });
}

/** Applies an assignee/tag filter (empty fields match everything). */
export function filterTasks(tasks: Task[], filter: TaskFilter): Task[] {
  const assignee = filter.assignee.trim().toLowerCase();
  const tag = filter.tag.trim().toLowerCase();
  return tasks.filter((t) => {
    if (assignee && (t.assignee ?? '').toLowerCase() !== assignee) return false;
    if (tag && !(t.tags ?? []).some((x) => x.toLowerCase() === tag))
      return false;
    return true;
  });
}

/** Unique, sorted assignee aliases present across the given tasks. */
export function collectAssignees(tasks: Task[]): string[] {
  const set = new Set<string>();
  for (const t of tasks) {
    const a = (t.assignee ?? '').trim();
    if (a) set.add(a);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Unique, sorted owner aliases present across the given tasks. */
export function collectOwners(tasks: Task[]): string[] {
  const set = new Set<string>();
  for (const t of tasks) {
    const o = (t.owner ?? '').trim();
    if (o) set.add(o);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * True when a task carries a usable estimate: either it is a Spike (estimated
 * as investigative work, not a day figure) or it has a positive effort-days
 * estimate from the estimation engine.
 */
export function isEstimated(task: Pick<Task, 'type' | 'effortDays'>): boolean {
  if (task.type === 'Spike') return true;
  return typeof task.effortDays === 'number' && task.effortDays > 0;
}

/**
 * Human-readable estimate label for the backlog/planning views:
 *   • 'Spike'          — the task is a Spike (time-boxed investigation).
 *   • '<n>d'           — the estimated engineering effort in ideal days.
 *   • 'Not Estimated'  — no estimate captured yet.
 */
export function formatTaskEstimate(
  task: Pick<Task, 'type' | 'effortDays'>,
): string {
  if (task.type === 'Spike') return 'Spike';
  if (typeof task.effortDays === 'number' && task.effortDays > 0) {
    return `${task.effortDays}d`;
  }
  return 'Not Estimated';
}

/**
 * Unplanned (ad-hoc) sprint scope: how many tasks were pulled in mid-sprint and
 * the ideal-days they add. Only {@link Task.adhoc} tasks count; Spikes (which
 * carry no day figure) contribute to the count but not the day total. Effort is
 * rounded to 2 decimals to avoid float noise in the header/insights readout.
 */
export function unplannedScope(
  tasks: Task[],
): { count: number; days: number } {
  let count = 0;
  let days = 0;
  for (const t of tasks ?? []) {
    if (!t.adhoc) continue;
    count += 1;
    if (typeof t.effortDays === 'number' && t.effortDays > 0) days += t.effortDays;
  }
  return { count, days: Math.round(days * 100) / 100 };
}

/** A single effort value in ideal days (missing/invalid/non-positive → 0). */
function effortDaysOf(task: Pick<Task, 'effortDays'>): number {
  const d = task.effortDays;
  return typeof d === 'number' && Number.isFinite(d) && d > 0 ? d : 0;
}

/**
 * The task's TOTAL effort in ideal days, used everywhere committed/assigned
 * effort is summed:
 *   • the task's own custom {@link Task.effortDays} when it carries one, OTHERWISE
 *   • the sum of its subtasks' effortDays (so a parent that was never estimated
 *     directly still counts its broken-down work).
 *
 * Subtasks are separate Task documents (linked via {@link Task.parentTaskId}),
 * so they are passed in explicitly; omit them (or pass none) to get just the
 * task's own effort. Missing/invalid/non-positive values contribute 0, and the
 * result is rounded to 2 decimals — so this is always safe for legacy tasks.
 */
export function totalEffortDays(
  task: Pick<Task, 'effortDays'>,
  subtasks?: Pick<Task, 'effortDays'>[],
): number {
  const own = effortDaysOf(task);
  if (own > 0) return own;
  const subtotal = (subtasks ?? []).reduce((sum, s) => sum + effortDaysOf(s), 0);
  return Math.round(subtotal * 100) / 100;
}

/** Unique, sorted tags present across the given tasks. */
export function collectTags(tasks: Task[]): string[] {
  const set = new Set<string>();
  for (const t of tasks) {
    for (const tag of t.tags ?? []) {
      const trimmed = tag.trim();
      if (trimmed) set.add(trimmed);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** True when a task is a subtask (its parentTaskId is set to a non-empty id). */
export function isSubtask(task: Pick<Task, 'parentTaskId'>): boolean {
  return !!(task.parentTaskId && task.parentTaskId.trim());
}

/** Keeps only TOP-LEVEL tasks (no parentTaskId) — board/backlog/by-person use. */
export function topLevelTasks(tasks: Task[]): Task[] {
  return (tasks ?? []).filter((t) => !isSubtask(t));
}

/** Done/total counts for a set of subtasks (DONE lane = done). */
export interface SubtaskProgress {
  done: number;
  total: number;
}

/** Computes done/total over a list of subtasks (a subtask is done in DONE lane). */
export function subtaskProgress(subtasks: Task[]): SubtaskProgress {
  const list = subtasks ?? [];
  return {
    total: list.length,
    done: list.filter((t) => (t.boardStatus ?? 'TODO') === 'DONE').length,
  };
}

/**
 * Rolls a flat task list into a parentId → {done,total} map, counting only the
 * tasks that ARE subtasks (parentTaskId set). Powers the parent card's subtask
 * badge without a per-parent fetch.
 */
export function subtaskProgressByParent(
  tasks: Task[],
): Map<string, SubtaskProgress> {
  const map = new Map<string, SubtaskProgress>();
  for (const t of tasks ?? []) {
    if (!isSubtask(t)) continue;
    const parentId = (t.parentTaskId ?? '').trim();
    if (!parentId) continue;
    const cur = map.get(parentId) ?? { done: 0, total: 0 };
    cur.total += 1;
    if ((t.boardStatus ?? 'TODO') === 'DONE') cur.done += 1;
    map.set(parentId, cur);
  }
  return map;
}

/** The subtask board-lane cycle order: TODO → IN_PROGRESS → DONE → TODO. */
export function nextSubtaskBoardStatus(current?: BoardStatus): BoardStatus {
  const order = BOARD_STATUS_ORDER;
  const idx = order.indexOf((current ?? 'TODO') as BoardStatus);
  return order[(idx + 1) % order.length];
}
